import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	FiRefreshCw,
	FiDollarSign,
	FiTrendingUp,
	FiShoppingCart,
	FiShoppingBag,
	FiUsers,
	FiTruck,
	FiRotateCcw,
	FiCornerUpLeft,
	FiPackage,
} from "react-icons/fi";
import {
	shopClient,
	eurCents,
	fmtDate,
	type ShopDashboardStats,
	type ShopOrderListItem,
} from "./shopClient";

/* ---------------------------------------------------------------- StatusBadge */

type Tone = "emerald" | "amber" | "slate" | "rose";

const TONE_CLASSES: Record<Tone, string> = {
	emerald: "bg-emerald-50 text-emerald-700",
	amber: "bg-amber-50 text-amber-700",
	slate: "bg-slate-100 text-slate-600",
	rose: "bg-rose-50 text-rose-600",
};

const STATUS_TONE: Record<string, Tone> = {
	Paid: "emerald",
	Fulfilled: "emerald",
	Pending: "amber",
	Refunded: "slate",
	Cancelled: "rose",
};

const STATUS_LABEL: Record<string, string> = {
	Pending: "En attente",
	Paid: "Payée",
	Fulfilled: "Expédiée",
	Refunded: "Remboursée",
	Cancelled: "Annulée",
};

function StatusBadge({ status }: { status: string }) {
	const tone = STATUS_TONE[status] ?? "slate";
	const label = STATUS_LABEL[status] ?? status;
	return (
		<span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}>
			{label}
		</span>
	);
}

/* ------------------------------------------------------------------- KpiCard */

function KpiCard({
	label,
	value,
	sub,
	icon,
}: {
	label: string;
	value: string;
	sub?: string;
	icon: React.ReactNode;
}) {
	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
					<p className="mt-2 truncate text-2xl font-semibold text-slate-900">{value}</p>
					{sub ? <p className="mt-1 truncate text-xs text-slate-500">{sub}</p> : null}
				</div>
				<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
					{icon}
				</span>
			</div>
		</div>
	);
}

/* --------------------------------------------------------------- StockStat */

function StockStat({ label, value, tone = "slate" }: { label: string; value: string; tone?: Tone }) {
	const toneCls: Record<Tone, string> = {
		emerald: "text-emerald-600",
		amber: "text-amber-600",
		rose: "text-rose-600",
		slate: "text-slate-900",
	};
	return (
		<div className="bg-white px-4 py-3">
			<p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
			<p className={`mt-1 text-lg font-semibold ${toneCls[tone]}`}>{value}</p>
		</div>
	);
}

/* --------------------------------------------------------------- SalesTrend */

function SalesTrend({ points }: { points: Array<{ date: string; orders: number; revenueCents: number }> }) {
	const max = Math.max(1, ...points.map((p) => p.revenueCents));
	return (
		<div className="flex items-end gap-1" style={{ height: 96 }}>
			{points.map((p) => (
				<div key={p.date} className="group relative flex-1" title={`${p.date} · ${eurCents(p.revenueCents)} · ${p.orders} cmd`}>
					<div
						className="w-full rounded-t bg-emerald-500/80 transition group-hover:bg-emerald-600"
						style={{ height: Math.max(2, Math.round((p.revenueCents / max) * 88)) }}
					/>
				</div>
			))}
		</div>
	);
}

/* ----------------------------------------------------------------- Dashboard */

function customerLabel(o: ShopOrderListItem): string {
	return o.customerName || o.customerEmail || "Invité";
}

export default function ShopDashboard() {
	const [stats, setStats] = useState<ShopDashboardStats | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await shopClient.dashboard.stats();
			setStats(data);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				{/* Header */}
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Tableau de bord boutique</h1>
						<p className="mt-1 text-sm text-slate-500">Vue d&apos;ensemble de votre activité</p>
					</div>
					<button
						type="button"
						onClick={() => void load()}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : undefined} />
						Actualiser
					</button>
				</div>

				{error ? (
					<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
						{error}
					</div>
				) : null}

				{loading && !stats ? (
					<div className="rounded-2xl border border-slate-100 bg-white px-5 py-16 text-center text-slate-400 shadow-sm">
						Chargement…
					</div>
				) : null}

				{stats ? (
					<>
						{/* KPI grid */}
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
							<KpiCard
								label="Chiffre d'affaires"
								value={eurCents(stats.revenueTotalCents)}
								icon={<FiDollarSign />}
							/>
							<KpiCard
								label="CA 30 jours"
								value={eurCents(stats.revenue30dCents)}
								icon={<FiTrendingUp />}
							/>
							<KpiCard
								label="Commandes"
								value={stats.ordersTotal.toLocaleString("fr-FR")}
								sub={`${stats.ordersPaid.toLocaleString("fr-FR")} payée${stats.ordersPaid > 1 ? "s" : ""}`}
								icon={<FiShoppingCart />}
							/>
							<KpiCard
								label="Panier moyen"
								value={eurCents(stats.averageOrderValueCents)}
								icon={<FiShoppingBag />}
							/>
							<KpiCard
								label="Clients"
								value={stats.customersCount.toLocaleString("fr-FR")}
								icon={<FiUsers />}
							/>
							<KpiCard
								label="À expédier"
								value={stats.ordersAwaitingFulfilment.toLocaleString("fr-FR")}
								icon={<FiTruck />}
							/>
							<KpiCard
								label="Consignes en attente"
								value={stats.consigneAwaitingCount.toLocaleString("fr-FR")}
								sub={eurCents(stats.consigneAwaitingCents)}
								icon={<FiCornerUpLeft />}
							/>
							<KpiCard
								label="Remboursées"
								value={stats.ordersRefunded.toLocaleString("fr-FR")}
								icon={<FiRotateCcw />}
							/>
							<KpiCard
								label="Unités vendues (30j)"
								value={stats.unitsSold30d.toLocaleString("fr-FR")}
								icon={<FiPackage />}
							/>
						</div>

						{/* Stock & inventory */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Stock &amp; inventaire</h2>
								<Link to="/stock" className="text-xs font-medium text-emerald-600 hover:underline">Gérer le stock →</Link>
							</div>
							<div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-5">
								<StockStat label="Références" value={stats.stock.totalSkus.toLocaleString("fr-FR")} />
								<StockStat label="Rupture" value={stats.stock.outOfStock.toLocaleString("fr-FR")} tone={stats.stock.outOfStock > 0 ? "rose" : "slate"} />
								<StockStat label="Stock faible" value={stats.stock.lowStock.toLocaleString("fr-FR")} tone={stats.stock.lowStock > 0 ? "amber" : "slate"} />
								<StockStat label="Unités en stock" value={stats.stock.unitsOnHand.toLocaleString("fr-FR")} />
								<StockStat label="Valeur du stock" value={eurCents(stats.stock.stockValueCents)} tone="emerald" />
							</div>
						</div>

						{/* Sales trend (30 days) */}
						{stats.salesTrend.length > 0 && (
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
								<div className="border-b border-slate-100 px-5 py-3">
									<h2 className="text-sm font-semibold text-slate-700">Ventes — 30 jours</h2>
								</div>
								<div className="px-5 py-4">
									<SalesTrend points={stats.salesTrend} />
								</div>
							</div>
						)}

						{/* Status breakdown */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Répartition des statuts</h2>
							</div>
							<div className="px-5 py-4">
								{stats.statusBreakdown.length === 0 ? (
									<p className="text-sm text-slate-400">Aucune donnée</p>
								) : (
									<div className="flex flex-wrap items-center gap-3">
										{stats.statusBreakdown.map((s) => (
											<div
												key={s.status}
												className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5"
											>
												<StatusBadge status={s.status} />
												<span className="text-sm font-semibold text-slate-900">
													{s.count.toLocaleString("fr-FR")}
												</span>
											</div>
										))}
									</div>
								)}
							</div>
						</div>

						{/* Two columns */}
						<div className="grid gap-6 lg:grid-cols-2">
							{/* Recent orders */}
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
								<div className="border-b border-slate-100 px-5 py-3">
									<h2 className="text-sm font-semibold text-slate-700">Commandes récentes</h2>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-slate-100 text-sm">
										<thead className="bg-slate-50">
											<tr>
												<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													#
												</th>
												<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Date
												</th>
												<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Client
												</th>
												<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Statut
												</th>
												<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Total
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-slate-100">
											{stats.recentOrders.length === 0 ? (
												<tr>
													<td colSpan={5} className="px-5 py-10 text-center text-slate-400">
														Aucune commande
													</td>
												</tr>
											) : (
												stats.recentOrders.map((o) => (
													<tr key={o.id} className="hover:bg-slate-50">
														<td className="px-5 py-3">
															<Link
																to={`/orders/${o.id}`}
																className="font-mono text-xs font-medium text-slate-900 hover:underline"
															>
																#{o.id.slice(0, 8)}
															</Link>
														</td>
														<td className="px-5 py-3 text-slate-500">{fmtDate(o.createdAt)}</td>
														<td className="px-5 py-3 text-slate-700">{customerLabel(o)}</td>
														<td className="px-5 py-3">
															<StatusBadge status={o.status} />
														</td>
														<td className="px-5 py-3 text-right font-medium text-slate-900">
															{eurCents(o.totalCents, o.currency)}
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>
							</div>

							{/* Top products */}
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
								<div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
									<FiPackage className="text-slate-400" />
									<h2 className="text-sm font-semibold text-slate-700">Meilleures ventes</h2>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-slate-100 text-sm">
										<thead className="bg-slate-50">
											<tr>
												<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Produit
												</th>
												<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													Qté
												</th>
												<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">
													CA
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-slate-100">
											{stats.topProducts.length === 0 ? (
												<tr>
													<td colSpan={3} className="px-5 py-10 text-center text-slate-400">
														Aucune vente
													</td>
												</tr>
											) : (
												stats.topProducts.map((p, i) => (
													<tr key={p.productContentId ?? `${p.title}-${i}`} className="hover:bg-slate-50">
														<td className="px-5 py-3 text-slate-700">{p.title}</td>
														<td className="px-5 py-3 text-right text-slate-700">
															{p.quantity.toLocaleString("fr-FR")}
														</td>
														<td className="px-5 py-3 text-right font-medium text-slate-900">
															{eurCents(p.revenueCents)}
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</>
				) : null}
			</div>
		</div>
	);
}
